import React from 'react';
import './StatisticsSection.css';
import { ProgressBar, Divider, TooltipTrigger, TagFeedback, Spinner } from '@spark';
import { calculatePercentage } from '@/features/datasets/components/DataSelection/RawRecordsStatus/utils';

/**
 * Component to display statistics about the raw records processing.
 * Built from Spark DS primitives (TagFeedback, Spinner, ProgressBar, Divider,
 * Tooltip).
 */
const StatisticsSection = ({ loading, statsData }) => {
    const total = statsData?.total || 0;

    const exampleFailed = statsData?.request_example_failed || 0;
    const exampleNotProcessed = statsData?.request_example_not_processed || 0;
    const exampleSuccessful = statsData?.request_example_success || 0;
    const exampleCurrentProcessing = statsData?.request_example_processing || 0;
    const exampleSuccessPercent = calculatePercentage(exampleSuccessful, total);

    const responseFailed = statsData?.response_failed || 0;
    const responseNotProcessed = statsData?.response_not_processed || 0;
    const responseSuccessful = statsData?.response_success || 0;
    const responseCurrentProcessing = statsData?.response_processing || 0;
    const responseSuccessPercent = calculatePercentage(responseSuccessful, total);

    const totalFailed = statsData?.failed || 0;
    const totalSuccessful = statsData?.successful || 0;

    const renderStatCard = (status, value, colorClass, progressPercent, description) => (
        <div className={`spark-stats-card spark-stats-card-${colorClass}`}>
            <p className="spark-stats-card-title">{status}</p>
            <p className="spark-stats-card-value">
                {value}
                <span className="spark-stats-card-percent">({progressPercent}%)</span>
            </p>
            <p className="spark-stats-card-desc">{description}</p>
            <ProgressBar
                value={progressPercent}
                type={colorClass === 'error' ? 'error' : 'default'}
                showPercentage={false}
                className="spark-stats-card-progress"
            />
        </div>
    );

    return (
        <div className="spark-stats-section">
            {loading && (
                <div className="spark-stats-loading">
                    <Spinner aria-label="Loading statistics" />
                </div>
            )}

            {/* Overview header */}
            <div className="spark-stats-overview-header">
                <p className="spark-stats-overview-title">Processing Status Overview</p>
                <div className="spark-stats-badges">
                    <TooltipTrigger text="Total Records">
                        <span className="spark-stats-badge-wrap">
                            <TagFeedback variant="processing" label={`${total} Records`} />
                        </span>
                    </TooltipTrigger>
                    <TooltipTrigger text="Successfully Processed">
                        <span className="spark-stats-badge-wrap">
                            <TagFeedback variant="success" showIcon label={`${totalSuccessful} Success`} />
                        </span>
                    </TooltipTrigger>
                    <TooltipTrigger text="Processing Failed">
                        <span className="spark-stats-badge-wrap">
                            <TagFeedback variant="error" showIcon label={`${totalFailed} Failed`} />
                        </span>
                    </TooltipTrigger>
                </div>
            </div>

            {/* Example Processing */}
            <div className="spark-stats-section-divider">
                <Divider />
                <p className="spark-stats-group-label">Example Processing</p>
            </div>
            <div className="spark-stats-cards-row">
                {renderStatCard('Successfully Processed', exampleSuccessful, 'success', exampleSuccessPercent, 'Examples that were successfully extracted')}
                {renderStatCard('Processing Failed', exampleFailed, 'error', calculatePercentage(exampleFailed, total), 'Examples that failed to be extracted')}
                {renderStatCard('Currently Processing', exampleCurrentProcessing, 'info', calculatePercentage(exampleCurrentProcessing, total), 'Examples being processed now')}
                {renderStatCard('Not Processed Yet', exampleNotProcessed, 'neutral', calculatePercentage(exampleNotProcessed, total), 'Examples waiting to be processed')}
            </div>

            {/* Response Parser Processing */}
            <div className="spark-stats-section-divider">
                <Divider />
                <p className="spark-stats-group-label">Response Parser Processing</p>
            </div>
            <div className="spark-stats-cards-row">
                {renderStatCard('Successfully Processed', responseSuccessful, 'success', responseSuccessPercent, 'Responses that were successfully parsed')}
                {renderStatCard('Processing Failed', responseFailed, 'error', calculatePercentage(responseFailed, total), 'Responses that failed to be parsed')}
                {renderStatCard('Currently Processing', responseCurrentProcessing, 'info', calculatePercentage(responseCurrentProcessing, total), 'Responses being processed now')}
                {renderStatCard('Not Processed Yet', responseNotProcessed, 'neutral', calculatePercentage(responseNotProcessed, total), 'Responses waiting to be processed')}
            </div>
        </div>
    );
};

export default StatisticsSection;
